import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

declare global {
	interface Window {
		lastFocusElement: any;
	}
}

interface ModalProps {
	show: boolean;
	onHide: () => void;
	heading: string;
	children: React.ReactChild;
	containerClasses?: string;
	containerId?: string;
	headingImg?: string;
	overlaycontainerClass?: string;
}

export const modalFocuser = (modalElement: any, modalHeading: string) => {
	if (modalElement) {
		document.body.classList.add('tab-key-press');
		modalElement.removeAttribute('role');
		modalElement.removeAttribute('aria-modal');
		modalElement.removeAttribute('tabindex');

		const modalHeaderElement: any = document.querySelector('.modal-header');
		const checkRadioButton = modalElement.querySelector('input[type="radio"]');

		if (modalHeaderElement) {
			modalHeaderElement.setAttribute('aria-label', modalHeading);
		}

		document.addEventListener('keydown', (event) => {
			const focusableElements = modalElement.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			const firstFocusableElement = focusableElements[0];
			const lastFocusableElement = focusableElements[focusableElements.length - 1];
			const lastPrevFocusableElement = focusableElements[focusableElements.length - 2];
			const isTabPressed = event.key === 'Tab';
			if (!isTabPressed) {
				return;
			}
			if (event.shiftKey) {
				if (document.activeElement === firstFocusableElement) {
					event.preventDefault();
					if (lastFocusableElement.disabled) {
						lastPrevFocusableElement.focus();
					} else {
						lastFocusableElement.focus();
					}
				}
			} else {
				if (lastFocusableElement.disabled) {
					if (
						document.activeElement === lastPrevFocusableElement ||
						document.activeElement === checkRadioButton
					) {
						firstFocusableElement.focus();
						event.preventDefault();
					}
				} else {
					if (document.activeElement === lastFocusableElement) {
						firstFocusableElement.focus();
						event.preventDefault();
					}
				}
			}
		});
	}
};

const Modal = ({
	show,
	onHide,
	heading,
	children,
	containerClasses = '',
	containerId = '',
	headingImg = '',
	overlaycontainerClass = ''
}: ModalProps) => {
	const closeButtonRef: any = useRef<HTMLInputElement>(null);
	const modalOverlayRef: any = useRef<HTMLInputElement>(null);
	const previousActiveElement: any = document.activeElement;
	const bindModal: any = document.querySelector('#bind-modal');
	const checkAndroidDevice = /(android)/i.test(navigator.userAgent);
	const mobileModalFocusRef: any = useRef<HTMLInputElement>(null);
	const setTabindex = checkAndroidDevice ? { tabIndex: -1 } : {};

	useEffect(() => {
		document.addEventListener('keyup', function (event) {
			if (event.key === 'Escape') {
				if (show) {
					onHide();
				}
			}
		});

		return () => {
			document.removeEventListener('keyup', onHide);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show]);

	useEffect(() => {
		const rootElement = document.getElementById('root');

		if (closeButtonRef.current && show) {
			if (checkAndroidDevice) {
				mobileModalFocusRef.current.focus();
			} else {
				closeButtonRef.current.focus();
			}
			document.body.classList.add('modal-popupopen');
			document.body.style.cssText = 'overflow:hidden; width:100%; top:0';
			modalFocuser(modalOverlayRef.current, heading);

			if (rootElement) {
				rootElement.setAttribute('aria-hidden', 'true');
			}
		}

		return () => {
			if (previousActiveElement && document.body.contains(previousActiveElement)) {
				previousActiveElement.focus();
			} else if (window.lastFocusElement && document.getElementById(window.lastFocusElement)) {
				document.getElementById(window.lastFocusElement)?.focus();
			}

			document.body.classList.remove('modal-popupopen');
			document.body.removeAttribute('style');

			if (rootElement) {
				rootElement.removeAttribute('aria-hidden');
			}
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show]);

	return ReactDOM.createPortal(
		<div
			id={containerId}
			ref={modalOverlayRef}
			className={'acc-modaloverlay ' + overlaycontainerClass}
			style={{ display: show ? 'block' : 'none', overflow: 'scroll' }}>
			<div
				className={'acc-modal modal_pop ' + containerClasses}
				role="dialog"
				aria-label={heading}
				{...setTabindex}
				ref={mobileModalFocusRef}>
				{heading && (
					<div className="acc-modal-head">
						{!containerClasses.includes('confirm-modal') && (
							<button
								ref={closeButtonRef}
								className="modal-close-icon"
								aria-label="Close"
								onClick={() => onHide()}></button>
						)}
						{containerClasses.includes('confirm-modal') && <div ref={closeButtonRef}></div>}
						{headingImg && (
							<div className="header-icon">
								<img src={headingImg} alt="logo" />
							</div>
						)}
						<h2 className="dialog-title-text">{heading}</h2>
					</div>
				)}

				{children}
			</div>
		</div>,
		bindModal
	);
};

export default Modal;
